/**
 * Motion for the cart drawer upsell (snippets/ambera-cart-upsell.liquid).
 *
 * Adding and removing still go through the theme: "Adaugă" is a real product form, "Șterge" is the
 * theme's own remove button, and the drawer re-renders through the theme's morph. This file only
 * watches that happen and animates the difference.
 *
 * Before an action it snapshots the upsell (which cards, where, a copy of each); right after the
 * morph it compares and animates:
 *
 * - Add from the upsell: the card's thumbnail flies on an arc into the new cart line; the line
 *   slides in under a warm highlight; the other lines glide down; the "(N)" count bumps.
 * - Remove a cart line: its thumbnail lifts off the line. If the product comes back into the upsell,
 *   the thumbnail flies into its card; otherwise it dissolves.
 * - Upsell cards: new ones fade in one after another, gone ones melt away, the rest glide over.
 * - The upsell itself: when its last card goes it draws shut like a curtain towards the cart (desktop
 *   rail) or folds away (mobile strip); when it gets its first card it slides out from behind the
 *   cart or unfolds.
 *
 * Everything that reads the new layout runs inside the MutationObserver callback that sees the
 * morph, before the browser paints, so nothing flashes in its final place first. With "reduce
 * motion" on, only short fades remain.
 */

const ADD_SELECTOR = '.ambera-cart-upsell__add';
const REMOVE_SELECTOR = '.cart-items__remove';
const CARD_SELECTOR = '.ambera-cart-upsell__item';
const DRAWER_SELECTOR = '.cart-drawer--ambera';
const ROW_SELECTOR = 'tbody .cart-items__table-row[data-key]';
const ROW_MEDIA_SELECTOR = '.cart-items__media-container, .cart-items__media';
const VARIANTS = /** @type {const} */ (['rail', 'strip']);
const TIMEOUT_MS = 6000;
const LINES_UPDATE_EVENT = 'shopify:cart:lines-update';

const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
const EASE_IN_OUT = 'cubic-bezier(0.65, 0, 0.35, 1)';

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* -------------------------------------------------------------------------------------------- */
/* Helpers                                                                                      */
/* -------------------------------------------------------------------------------------------- */

/** @param {string | null | undefined} href */
const pathOf = (href) => {
  if (!href) return '';
  try {
    return decodeURI(new URL(href, window.location.origin).pathname).replace(/\/$/, '');
  } catch {
    return href;
  }
};

/** @param {Element} el */
const rectOf = (el) => el.getBoundingClientRect();

/** @param {DOMRect} rect */
const laidOut = (rect) => rect.width > 0 && rect.height > 0;

/** @param {Element} el */
const dropIds = (el) => {
  el.removeAttribute('id');
  for (const child of el.querySelectorAll('[id]')) child.removeAttribute('id');
  return el;
};

/**
 * Overlays (flying thumbnails, melting cards) live inside the drawer. It is a modal `<dialog>` in
 * the browser's top layer: anything appended to `body` paints underneath it whatever its z-index.
 * @param {Element} drawer
 */
function overlayLayer(drawer) {
  const host = drawer.closest('dialog') ?? drawer;
  const outer = rectOf(host);
  // Absolute children are placed against the padding box, inside the drawer's left border.
  const box = { left: outer.left + host.clientLeft, top: outer.top + host.clientTop };
  return {
    /**
     * @param {HTMLElement} el
     * @param {DOMRect} rect - Viewport rect to put the element on
     */
    place(el, rect) {
      Object.assign(el.style, {
        position: 'absolute',
        left: `${rect.left - box.left}px`,
        top: `${rect.top - box.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        margin: '0',
        pointerEvents: 'none',
        boxSizing: 'border-box',
      });
      host.appendChild(el);
    },
  };
}

/**
 * FLIP: the element has already moved; play it from where it was.
 * @param {Element} el
 * @param {DOMRect} before
 * @param {number} [delay]
 */
function glide(el, before, delay = 0) {
  const after = rectOf(el);
  const dx = before.left - after.left;
  const dy = before.top - after.top;
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
  el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }], {
    duration: 480,
    delay,
    easing: EASE_OUT,
    fill: 'backwards',
  });
}

/**
 * A thumbnail-sized tile carrying the product image, used for every flight.
 * @param {ReturnType<typeof overlayLayer>} layer
 * @param {string} src
 * @param {DOMRect} rect
 */
function makeFlyer(layer, src, rect) {
  const flyer = document.createElement('div');
  flyer.className = 'ambera-cart-upsell-flyer';
  flyer.style.backgroundImage = `url("${src}")`;
  layer.place(flyer, rect);
  return flyer;
}

/**
 * Flies an overlay from where it sits now to `to`, on an arc, resizing on the way.
 * @param {HTMLElement} flyer
 * @param {DOMRect} from - Where the flyer is drawn at the start (may include a lift transform)
 * @param {DOMRect} to
 * @param {string} [startTransform] - Transform the flyer currently shows (keeps the motion seamless)
 */
function flyTo(flyer, from, to, startTransform = 'translate(0, 0) scale(1, 1)') {
  // Work in the flyer's own untransformed box (transform-origin is its top-left corner).
  const host = flyer.parentElement ?? document.body;
  const hostRect = rectOf(host);
  const boxLeft = hostRect.left + host.clientLeft + parseFloat(flyer.style.left);
  const boxTop = hostRect.top + host.clientTop + parseFloat(flyer.style.top);
  const boxWidth = parseFloat(flyer.style.width);
  const boxHeight = parseFloat(flyer.style.height);

  const dx = to.left - from.left;
  const dy = to.top - from.top;
  const toX = to.left - boxLeft;
  const toY = to.top - boxTop;
  const fromX = from.left - boxLeft;
  const fromY = from.top - boxTop;
  const toSx = to.width / boxWidth;
  const toSy = to.height / boxHeight;
  const fromSx = from.width / boxWidth;
  const fromSy = from.height / boxHeight;
  const lift = Math.min(90, Math.max(40, Math.hypot(dx, dy) * 0.18));
  const midSx = (fromSx + toSx) / 2 + 0.08;
  const midSy = (fromSy + toSy) / 2 + 0.08;

  const animation = flyer.animate(
    [
      { transform: startTransform, boxShadow: '0 10px 24px rgb(19 19 19 / 16%)' },
      {
        transform: `translate(${(fromX + toX) / 2}px, ${(fromY + toY) / 2 - lift}px) scale(${midSx}, ${midSy}) rotate(-4deg)`,
        boxShadow: '0 18px 40px rgb(19 19 19 / 22%)',
        offset: 0.5,
      },
      { transform: `translate(${toX}px, ${toY}px) scale(${toSx}, ${toSy}) rotate(0deg)`, boxShadow: '0 0 0 rgb(0 0 0 / 0)' },
    ],
    { duration: 720, easing: EASE_IN_OUT, fill: 'forwards' }
  );
  return animation.finished.then(
    () => flyer.remove(),
    () => flyer.remove()
  );
}

/**
 * Hides `media` until `landed` settles, then fades it in under the arriving flyer.
 * @param {Element | null} media
 * @param {Promise<void>} landed
 */
function revealOnLanding(media, landed) {
  if (!(media instanceof HTMLElement)) return;
  media.style.opacity = '0';
  landed.finally(() => {
    media.style.opacity = '';
    media.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
  });
}

/** @param {Element} drawer */
function bumpCount(drawer) {
  const badge = drawer.querySelector('.theme-drawer__badge');
  if (!badge) return;
  badge.animate(
    [
      { transform: 'scale(1)' },
      { transform: 'scale(1.3)', color: 'var(--color-primary-500)', offset: 0.35 },
      { transform: 'scale(1)' },
    ],
    { duration: 520, delay: 420, easing: EASE_OUT }
  );
}

/* -------------------------------------------------------------------------------------------- */
/* Upsell snapshot and diff                                                                     */
/* -------------------------------------------------------------------------------------------- */

/**
 * @typedef {{ rect: DOMRect, clone: HTMLElement, path: string }} CardShot
 * @typedef {{ visible: boolean, rect: DOMRect, clone: HTMLElement | null }} ContainerShot
 * @typedef {{ cards: Map<string, CardShot>, containers: Record<string, ContainerShot>, signature: string }} UpsellShot
 */

/** Ids of the cards currently in the DOM, both variants, as one comparable string. */
function upsellSignature() {
  return [...document.querySelectorAll(CARD_SELECTOR)].map((card) => card.id).sort().join('|');
}

/** @param {string} variant */
const containerOf = (variant) => document.querySelector(`.ambera-cart-upsell--${variant}:not(.ambera-cart-upsell--ghost)`);

/** @param {Element | null} container */
const isShowing = (container) =>
  !!container && !!container.querySelector(CARD_SELECTOR) && laidOut(rectOf(container));

/** @returns {UpsellShot} */
function snapshotUpsell() {
  /** @type {Map<string, CardShot>} */
  const cards = new Map();
  for (const card of document.querySelectorAll(CARD_SELECTOR)) {
    const rect = rectOf(card);
    if (!laidOut(rect) || !(card instanceof HTMLElement)) continue;
    cards.set(card.id, { rect, clone: /** @type {HTMLElement} */ (card.cloneNode(true)), path: pathOf(card.dataset.productUrl) });
  }

  /** @type {Record<string, ContainerShot>} */
  const containers = {};
  for (const variant of VARIANTS) {
    const el = containerOf(variant);
    const visible = isShowing(el);
    containers[variant] = {
      visible,
      rect: el ? rectOf(el) : new DOMRect(),
      clone: visible && el ? /** @type {HTMLElement} */ (el.cloneNode(true)) : null,
    };
  }

  return { cards, containers, signature: upsellSignature() };
}

/**
 * Draws a copy of the vanished upsell where it was, and closes it.
 * @param {string} variant
 * @param {ContainerShot} shot
 * @param {number} delay
 */
function closeContainer(variant, shot, delay) {
  const live = containerOf(variant);
  if (!shot.clone || !live?.parentElement) return;

  const ghost = dropIds(shot.clone);
  ghost.classList.add('ambera-cart-upsell--ghost');
  ghost.removeAttribute('data-hydration-key');
  ghost.setAttribute('aria-hidden', 'true');
  ghost.inert = true;
  live.parentElement.insertBefore(ghost, live);

  let animation;
  if (variant === 'rail') {
    // A curtain drawn shut towards the cart: the visible area shrinks into the drawer's edge.
    animation = ghost.animate(
      [
        { clipPath: 'inset(0 0 0 0)', transform: 'translateX(0)' },
        { clipPath: 'inset(0 0 0 100%)', transform: 'translateX(24px)' },
      ],
      { duration: 560, delay, easing: EASE_IN_OUT, fill: 'both' }
    );
  } else {
    // In the flow: fold the height away so everything below rises smoothly.
    Object.assign(ghost.style, { height: `${shot.rect.height}px`, overflow: 'hidden', boxSizing: 'border-box' });
    animation = ghost.animate(
      [
        { height: `${shot.rect.height}px`, paddingBlock: getComputedStyle(live).paddingBlock, opacity: 1 },
        { height: '0px', paddingBlock: '0px', opacity: 0 },
      ],
      { duration: 460, delay, easing: EASE_IN_OUT, fill: 'both' }
    );
  }
  animation.finished.finally(() => ghost.remove());
}

/**
 * Opens a freshly shown upsell and brings its cards in one after another.
 * @param {string} variant
 * @param {Element} container
 */
function openContainer(variant, container) {
  if (variant === 'rail') {
    container.animate(
      [
        { clipPath: 'inset(0 0 0 100%)', transform: 'translateX(24px)' },
        { clipPath: 'inset(0 0 0 0)', transform: 'translateX(0)' },
      ],
      { duration: 600, easing: EASE_OUT, fill: 'backwards' }
    );
  } else {
    const height = rectOf(container).height;
    const padding = getComputedStyle(container).paddingBlock;
    container.animate(
      [
        { height: '0px', paddingBlock: '0px', opacity: 0, overflow: 'hidden' },
        { height: `${height}px`, paddingBlock: padding, opacity: 1, overflow: 'hidden' },
      ],
      { duration: 480, easing: EASE_OUT, fill: 'backwards' }
    );
  }

  let index = 0;
  for (const card of container.querySelectorAll(CARD_SELECTOR)) {
    card.animate(
      [
        { opacity: 0, transform: variant === 'rail' ? 'translateX(-14px)' : 'translateY(10px)' },
        { opacity: 1, transform: 'translate(0, 0)' },
      ],
      { duration: 420, delay: 220 + 70 * index++, easing: EASE_OUT, fill: 'backwards' }
    );
  }
}

/**
 * Cards that left while their upsell stays open: a copy melts away where the card was.
 * @param {ReturnType<typeof overlayLayer>} layer
 * @param {CardShot} shot
 */
function meltCard(layer, shot) {
  const ghost = dropIds(shot.clone);
  ghost.classList.remove('is-adding');
  ghost.classList.add('ambera-cart-upsell__item--ghost');
  ghost.style.zIndex = '2';
  layer.place(ghost, shot.rect);
  ghost
    .animate(
      [
        { opacity: 0.55, transform: 'translateX(0) scale(1)', filter: 'blur(0)' },
        { opacity: 0, transform: 'translateX(-24px) scale(0.94)', filter: 'blur(2px)' },
      ],
      { duration: 360, easing: EASE_IN_OUT, fill: 'forwards' }
    )
    .finished.finally(() => ghost.remove());
}

/**
 * Animates everything that changed in the upsell since `before`.
 * @param {Element} drawer
 * @param {UpsellShot} before
 * @param {{ closeDelay?: number }} [options]
 * @returns {{ entered: { card: HTMLElement, mediaRect: DOMRect }[] }} The cards that appeared (both
 *   variants), with their thumbnail's final box measured before any entrance motion started
 */
function animateUpsellChange(drawer, before, options = {}) {
  const layer = overlayLayer(drawer);
  /** @type {{ card: HTMLElement, mediaRect: DOMRect }[]} */
  const entered = [];
  /** @param {Element} card */
  const note = (card) => {
    if (!(card instanceof HTMLElement)) return;
    entered.push({ card, mediaRect: rectOf(card.querySelector('.ambera-cart-upsell__media') ?? card) });
  };

  for (const variant of VARIANTS) {
    const was = before.containers[variant];
    const live = containerOf(variant);
    const now = isShowing(live);

    if (was?.visible && !now) {
      closeContainer(variant, was, options.closeDelay ?? 0);
      continue;
    }

    if (!was?.visible && now && live) {
      live.querySelectorAll(CARD_SELECTOR).forEach(note);
      openContainer(variant, live);
      continue;
    }

    if (!now || !live) continue;

    // Open before and after: per card.
    let enterIndex = 0;
    let glideIndex = 0;
    for (const card of live.querySelectorAll(CARD_SELECTOR)) {
      const shot = before.cards.get(card.id);
      if (shot) {
        glide(card, shot.rect, 40 * glideIndex++);
      } else {
        note(card);
        card.animate(
          [
            { opacity: 0, transform: 'scale(0.96) translateY(-8px)' },
            { opacity: 1, transform: 'scale(1) translateY(0)' },
          ],
          { duration: 420, delay: 140 + 70 * enterIndex++, easing: EASE_OUT, fill: 'backwards' }
        );
      }
    }

    // Card ids carry their variant ("ambera-cart-upsell-rail-<product id>").
    for (const [id, shot] of before.cards) {
      if (id.startsWith(`ambera-cart-upsell-${variant}-`) && !document.getElementById(id)) meltCard(layer, shot);
    }
  }

  return { entered };
}

/* -------------------------------------------------------------------------------------------- */
/* Flows                                                                                        */
/* -------------------------------------------------------------------------------------------- */

/**
 * Calls `onChange` inside the MutationObserver callback once `ready()` holds, or `onTimeout`.
 * @param {Element} drawer
 * @param {() => boolean} ready
 * @param {() => void} onChange
 * @param {() => void} [onTimeout]
 * @returns {{ stop: () => void, done: () => boolean }}
 */
function afterMorph(drawer, ready, onChange, onTimeout) {
  let finished = false;
  const observer = new MutationObserver(() => {
    if (finished || !ready()) return;
    finish();
    onChange();
  });
  const finish = () => {
    finished = true;
    observer.disconnect();
    window.clearTimeout(timer);
  };
  const timer = window.setTimeout(() => {
    if (finished) return;
    finish();
    onTimeout?.();
  }, TIMEOUT_MS);
  observer.observe(drawer.closest('dialog') ?? drawer, { childList: true, subtree: true });
  return { stop: finish, done: () => finished };
}

/**
 * "Adaugă" in the upsell.
 * @param {HTMLElement} card
 * @param {Element} drawer
 */
function onAdd(card, drawer) {
  if (card.classList.contains('is-adding')) return;

  const productPath = pathOf(card.dataset.productUrl);
  const image = card.querySelector('img');
  const media = card.querySelector('.ambera-cart-upsell__media');
  const mediaRect = rectOf(media ?? card);
  const cardId = card.id;
  const upsell = snapshotUpsell();
  const rowsBefore = new Map([...drawer.querySelectorAll(ROW_SELECTOR)].map((row) => [row.getAttribute('data-key'), rectOf(row)]));

  card.classList.add('is-adding');

  /** @returns {HTMLElement | undefined} */
  const findNewRow = () =>
    /** @type {HTMLElement | undefined} */ (
      [...drawer.querySelectorAll(ROW_SELECTOR)].find((row) => {
        if (rowsBefore.has(row.getAttribute('data-key'))) return false;
        return pathOf(row.querySelector('.cart-items__title')?.getAttribute('href')) === productPath;
      })
    );

  afterMorph(
    drawer,
    () => !!findNewRow(),
    () => {
      const row = findNewRow();
      if (!row) return;

      if (reducedMotion()) {
        row.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease-out' });
        return;
      }

      row.scrollIntoView({ block: 'nearest' });
      const layer = overlayLayer(drawer);
      const rowMedia = row.querySelector(ROW_MEDIA_SELECTOR) ?? row;

      // Thumbnail → new line.
      const src = image ? image.currentSrc || image.src : '';
      const landed = src
        ? flyTo(makeFlyer(layer, src, mediaRect), mediaRect, rectOf(rowMedia))
        : Promise.resolve();
      revealOnLanding(rowMedia, landed);

      // New line: slide in under a warm wash.
      row.animate(
        [
          { opacity: 0, transform: 'translateX(-18px)' },
          { opacity: 1, transform: 'translateX(0)' },
        ],
        { duration: 520, delay: 160, easing: EASE_OUT, fill: 'backwards' }
      );
      row.classList.add('ambera-cart-row--new');
      window.setTimeout(() => row.classList.remove('ambera-cart-row--new'), 1600);

      for (const other of drawer.querySelectorAll(ROW_SELECTOR)) {
        const before = rowsBefore.get(other.getAttribute('data-key'));
        if (before && other !== row) glide(other, before);
      }

      // The upsell: the added card melts (or the whole upsell closes, a beat after take-off).
      animateUpsellChange(drawer, upsell, { closeDelay: 260 });
      bumpCount(drawer);
    },
    () => document.getElementById(cardId)?.classList.remove('is-adding')
  );
}

/**
 * "Șterge" on a cart line.
 * @param {HTMLElement} row
 * @param {Element} drawer
 * @param {number} since - The click's timeStamp
 */
function onRemove(row, drawer, since) {
  const upsell = snapshotUpsell();
  const productPath = pathOf(row.querySelector('.cart-items__title')?.getAttribute('href'));
  const rowCount = drawer.querySelectorAll(ROW_SELECTOR).length;
  const motion = !reducedMotion();

  // Lift the line's thumbnail off. Not for the last line: the theme swaps the whole drawer to its
  // empty state with a view transition, and an overlay would be caught in that snapshot.
  /** @type {HTMLElement | null} */
  let flyer = null;
  /** @type {DOMRect | null} */
  let liftedRect = null;
  const LIFT = 'translate(0, -8px) scale(1.06, 1.06)';
  const rowMedia = row.querySelector(ROW_MEDIA_SELECTOR);
  const rowImage = row.querySelector('img');
  if (motion && rowCount > 1 && rowMedia && rowImage) {
    const rect = rectOf(rowMedia);
    flyer = makeFlyer(overlayLayer(drawer), rowImage.currentSrc || rowImage.src, rect);
    flyer.animate(
      [
        { transform: 'translate(0, 0) scale(1, 1)', boxShadow: '0 0 0 rgb(0 0 0 / 0)' },
        { transform: LIFT, boxShadow: '0 10px 24px rgb(19 19 19 / 16%)' },
      ],
      { duration: 260, easing: EASE_OUT, fill: 'forwards' }
    );
    // Where LIFT draws it: scaled from the top-left corner, 8px up.
    liftedRect = new DOMRect(rect.left, rect.top - 8, rect.width * 1.06, rect.height * 1.06);
    if (rowMedia instanceof HTMLElement) rowMedia.style.opacity = '0';
  }

  const dissolve = () => {
    if (!flyer) return;
    const leaving = flyer;
    flyer = null;
    // No start keyframe: it may begin while the lift is still running, and picks up from there.
    leaving
      .animate(
        [{ transform: 'translate(0, -14px) scale(0.82, 0.82)', opacity: 0 }],
        { duration: 320, easing: EASE_IN_OUT, fill: 'forwards' }
      )
      .finished.finally(() => leaving.remove());
  };

  // Nothing left in the cart would suggest this product: it cannot come back into the upsell, so
  // the thumbnail leaves with its line instead of hovering until the server answers.
  if (!mayReturnToUpsell(row, drawer, productPath)) dissolve();

  const watch = afterMorph(
    drawer,
    () => upsellSignature() !== upsell.signature,
    () => {
      if (!motion) {
        dissolve();
        return;
      }
      const { entered } = animateUpsellChange(drawer, upsell);
      const target = entered.find(
        ({ card, mediaRect }) => laidOut(mediaRect) && pathOf(card.dataset.productUrl) === productPath
      );
      const targetMedia = target?.card.querySelector('.ambera-cart-upsell__media');
      if (flyer && liftedRect && target && targetMedia) {
        const landing = flyTo(flyer, liftedRect, target.mediaRect, LIFT);
        flyer = null;
        revealOnLanding(targetMedia, landing);
      } else {
        dissolve();
      }
    },
    dissolve
  );

  // The theme resolves the update promise right before it morphs. If the upsell did not change,
  // nothing above fires: let the lifted thumbnail go once the update has settled.
  onLinesUpdate(since, (promise) => {
    const settle = () =>
      window.setTimeout(() => {
        if (watch.done()) return;
        watch.stop();
        dissolve();
      }, 80);
    if (promise) promise.then(settle, settle);
    else settle();
  });
}

/**
 * Whether any cart line other than `row` lists the product at `productPath` among its upsell
 * suggestions (`data-upsell-sources`, rendered by the snippet). Unknown means yes.
 * @param {HTMLElement} row
 * @param {Element} drawer
 * @param {string} productPath
 */
function mayReturnToUpsell(row, drawer, productPath) {
  const raw = VARIANTS.map((variant) => containerOf(variant)?.getAttribute('data-upsell-sources')).find(Boolean);
  if (!raw || !productPath) return true;
  /** @type {Record<string, string[]>} */
  let sources;
  try {
    sources = JSON.parse(raw);
  } catch {
    return true;
  }
  const byPath = new Map(Object.entries(sources).map(([path, list]) => [pathOf(path), list.map(pathOf)]));
  return [...drawer.querySelectorAll(ROW_SELECTOR)].some((other) => {
    if (other === row) return false;
    const path = pathOf(other.querySelector('.cart-items__title')?.getAttribute('href'));
    return path !== productPath && (byPath.get(path) ?? []).includes(productPath);
  });
}

/**
 * The theme's own click handler runs before ours and dispatches the lines-update event
 * synchronously, so a listener added from our click handler would miss it (the thumbnail then
 * waited for TIMEOUT_MS). Keep the latest event from a permanent listener instead.
 * @type {{ timeStamp: number, promise?: Promise<unknown> } | null}
 */
let lastLinesUpdate = null;

/**
 * Calls `callback` with the promise of the first lines-update dispatched at or after `since`
 * (an event timeStamp), whether it has already happened or is still to come.
 * @param {number} since
 * @param {(promise: Promise<unknown> | undefined) => void} callback
 */
function onLinesUpdate(since, callback) {
  if (lastLinesUpdate && lastLinesUpdate.timeStamp >= since) {
    callback(lastLinesUpdate.promise);
    return;
  }
  /** @param {Event} event */
  const listener = (event) => {
    document.removeEventListener(LINES_UPDATE_EVENT, listener, true);
    callback(/** @type {{ promise?: Promise<unknown> }} */ (/** @type {unknown} */ (event)).promise);
  };
  document.addEventListener(LINES_UPDATE_EVENT, listener, true);
}

/** @param {MouseEvent} event */
function handleClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const drawer = document.querySelector(DRAWER_SELECTOR);
  if (!drawer) return;

  const add = target.closest(ADD_SELECTOR);
  if (add) {
    const card = add.closest(CARD_SELECTOR);
    if (card instanceof HTMLElement) onAdd(card, drawer);
    return;
  }

  const remove = target.closest(REMOVE_SELECTOR);
  if (remove && drawer.contains(remove)) {
    const row = remove.closest(ROW_SELECTOR);
    if (row instanceof HTMLElement) onRemove(row, drawer, event.timeStamp);
  }
}

if (!window.__amberaCartUpsellAnimation) {
  window.__amberaCartUpsellAnimation = true;
  document.addEventListener(
    LINES_UPDATE_EVENT,
    (event) => {
      const { promise } = /** @type {{ promise?: Promise<unknown> }} */ (/** @type {unknown} */ (event));
      lastLinesUpdate = { timeStamp: event.timeStamp, promise };
    },
    true
  );
  // Capture phase: take the snapshot before the theme's handlers start the request.
  document.addEventListener('click', handleClick, { capture: true });
}
